import { Box, ExpandableSection, FormField, Grid, SpaceBetween } from '@amzn/awsui-components-core';
import {
  PublicationSearchResultItem,
  SearchPublicationsResponse,
} from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import { IMplServiceClient } from '../../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';
import * as UtilApi from '../../../api/UtilsApi';
import constants from '../../../common/constants';
import { ViewerType } from '../../../model/Enum';
import DownloadPdfButton from '../../common/DownloadPdfButton';
import DuplicationCheck from '../../utils/DuplicationCheck';
import { decodeSearchtextString } from '../../utils/Search';
import { ContentViewerContextData, withContentViewerContext } from '../ContentViewerContext';
import EditorWrapper from '../../../editor/EditorWrapper';
import { TiptapMenuType } from '../../../editor/utils';
import {
  TiptapOutputType,
  convertEntities,
  convertStringToTiptapOutput,
  getTextWithEntityPlaceholders,
} from '@amzn/awscat-editor';
import AppContextProps from '../../../contexts/AppContextProps';
import { withAppContext } from '../../../contexts/AppContext';
import InputSizeMessage from '../../utils/InputSizeMessage';
import { replaceNBSPWithSpace } from '../../../api/ContentUtil';

interface Props extends AppContextProps {
  onTitleUpdated?: (sectionId: string, title: any, validationError?: string) => void;
  readonly: boolean;
  title: string;
  shortTitle: string;
  stretch?: boolean;
  duplicationCheckEnabled?: boolean;
  contentId?: string;
  workflowId?: number;
  isReadOnlyPublication?: boolean;
  contentViewerContext: ContentViewerContextData;
}

interface State {
  isSearchingDuplicates: boolean;
  duplicateContents: PublicationSearchResultItem[];
  numberOfDuplicatesToShow: number;
}

class ContentTitle extends React.Component<Props, State> {
  // @ts-expect-error TS7008 - strict migration
  private duplicateSearchTimer;
  private mplService: IMplServiceClient;
  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    this.mplService = MplServiceClientFactory.create();
    this.duplicateSearchTimer = null;
    this.state = {
      duplicateContents: [],
      isSearchingDuplicates: false,
      numberOfDuplicatesToShow: UtilApi.defaultNumberOfDuplicatesToShow,
    };
  }

  public render() {
    const {
      readonly,
      title,
      shortTitle,
      stretch,
      duplicationCheckEnabled,
      contentId,
      workflowId,
      appContext,
    } = this.props;
    const { isSearchingDuplicates, duplicateContents, numberOfDuplicatesToShow } = this.state;
    const { entities } = appContext;

    // TODO: configure placeholder in tiptap editor
    const contentNameEditor = readonly ? (
      convertEntities(replaceNBSPWithSpace(title), entities)
    ) : (
      <>
        <EditorWrapper
          model={convertStringToTiptapOutput(title)}
          readOnly={false}
          onModelChange={this.handleLongTitleInput}
          menuType={TiptapMenuType.Hide}
          removeFormatting
          singleLine
        />
        <InputSizeMessage
          input={title}
          maxNum={UtilApi.longTitleInputMaxLength}
          minNum={UtilApi.longTitleInputMinLength}
        />
      </>
    );
    const contentShortNameEditor = (
      <>
        <EditorWrapper
          model={convertStringToTiptapOutput(shortTitle)}
          readOnly={false}
          onModelChange={this.handleShortTitleInput}
          menuType={TiptapMenuType.Hide}
          removeFormatting
          singleLine
        />
        <InputSizeMessage
          input={shortTitle}
          maxNum={UtilApi.shortTitleInputMaxLength}
          minNum={UtilApi.shortTitleInputMinLength}
        />
      </>
    );

    if (readonly) {
      return (
        <Box variant="div" margin={{ bottom: 's' }}>
          <SpaceBetween size="s" direction="horizontal">
            <Box variant="h2" fontSize="display-l">
              {contentNameEditor}
            </Box>
            {this.isAllowDownloadPdf() ? (
              <DownloadPdfButton
                title={convertEntities(title)}
                // @ts-expect-error TS2322 - strict migration
                contentId={contentId}
                workflowId={workflowId}
              />
            ) : null}
          </SpaceBetween>
        </Box>
      );
    } else {
      const duplicationCheck = duplicationCheckEnabled ? (
        <DuplicationCheck
          contentName={title}
          isSearchingDuplicates={isSearchingDuplicates}
          duplicateContents={duplicateContents}
          numberOfDuplicatesToShow={numberOfDuplicatesToShow}
          handleShowMoreDuplicatesClick={this.handleShowMoreDuplicatesClick}
        />
      ) : null;
      return (
        <Box padding={{ bottom: 'm' }}>
          <ExpandableSection
            headerText={constants.CONTENT_NAME_LABEL}
            headingTagOverride="h2"
            defaultExpanded
            variant="container"
          >
            <div id="title-section">
              <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
                <div className="edit-content-name">
                  <FormField
                    label={constants.LONG_TITLE_LABEL}
                    description={constants.LONG_TITLE_DESCRIPTION}
                    stretch={!!stretch}
                  >
                    {contentNameEditor}
                  </FormField>
                </div>
                <div className="edit-content-short-name">
                  <FormField
                    label={
                      <>
                        {constants.SHORT_TITLE_LABEL} - <i>optional</i>
                      </>
                    }
                    description={constants.SHORT_TITLE_DESCRIPTION}
                    stretch={!!stretch}
                  >
                    {contentShortNameEditor}
                  </FormField>
                </div>
              </Grid>
            </div>
            {duplicationCheck}
          </ExpandableSection>
        </Box>
      );
    }
  }

  private isAllowDownloadPdf = () => {
    const viewerType = this.props.contentViewerContext?.viewerType;
    // @ts-expect-error TS2345 - strict migration
    return [ViewerType.History, ViewerType.PublicationReadonly].includes(viewerType);
  };

  private handleLongTitleInput = async (value: TiptapOutputType) => {
    const valueString = getTextWithEntityPlaceholders(value);
    const error = this.validateTitleInput(valueString, 'contentName');
    const { onTitleUpdated, duplicationCheckEnabled } = this.props;

    if (onTitleUpdated) {
      onTitleUpdated('contentName', valueString, error);
    }

    if (duplicationCheckEnabled) {
      await this.executeDuplicationCheck(valueString);
    }
  };

  private handleShortTitleInput = (value: TiptapOutputType) => {
    const valueString = getTextWithEntityPlaceholders(value);
    const error = this.validateTitleInput(valueString, 'contentShortName', false);
    const { onTitleUpdated } = this.props;

    if (onTitleUpdated) {
      onTitleUpdated('contentShortName', valueString, error);
    }
  };

  private validateTitleInput = (value: string, sectionId: string, required: boolean = true) => {
    let error: string | undefined;

    // Use different validation limits based on field type
    const isLongTitle = sectionId === 'contentName';
    const minLength = isLongTitle
      ? UtilApi.longTitleInputMinLength
      : UtilApi.shortTitleInputMinLength;
    const maxLength = isLongTitle
      ? UtilApi.longTitleInputMaxLength
      : UtilApi.shortTitleInputMaxLength;

    if ((value.length < minLength && value.length > 0) || value.length > maxLength) {
      error = `Must be between ${minLength} and ${maxLength} characters`;
    }

    if (!value && required) {
      error = constants.REQUIRED;
    }
    return error;
  };

  private executeDuplicationCheck = async (title: string) => {
    const { contentId, appContext } = this.props;
    const { entities } = appContext;
    this.setState({
      isSearchingDuplicates: true,
      numberOfDuplicatesToShow: UtilApi.defaultNumberOfDuplicatesToShow,
    });
    clearTimeout(this.duplicateSearchTimer);
    this.duplicateSearchTimer = setTimeout(async () => {
      const searchResults = (await this.mplService.searchPublicationsByField(
        decodeSearchtextString(convertEntities(title, entities)),
        'name',
      )) as SearchPublicationsResponse;
      this.setState({
        duplicateContents: contentId
          ? searchResults.publications.filter((p) => p.contentId !== contentId)
          : searchResults.publications,
        isSearchingDuplicates: false,
      });
    }, UtilApi.duplicateSearchTimerDelay);
  };

  private handleShowMoreDuplicatesClick = () => {
    this.setState({
      numberOfDuplicatesToShow:
        this.state.numberOfDuplicatesToShow + UtilApi.numberOfDuplicatesToAdd,
    });
  };
}

export default withContentViewerContext(withAppContext(ContentTitle));
